import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, InputGroup, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageHeader from 'components/common/PageHeader';
import axiosInstance from 'axiosInstance';
import { useAuth } from 'context/AuthContext';
import SalesQuickCreateModal from './components/SalesQuickCreateModal';

const SalesCompaniesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [quickCreateMode, setQuickCreateMode] = useState('');
  const [generatedLink, setGeneratedLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);

  const canGenerateSalesLinks = hasPermission('crm.generate_link');

  useEffect(() => {
    let mounted = true;

    const loadCompanies = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axiosInstance.get('sales/companies/');
        if (!mounted) return;
        setCompanies(response.data?.companies || []);
      } catch (requestError) {
        if (!mounted) return;
        setError(requestError?.response?.data?.error || t('SALES.ERROR_LOAD_COMPANIES'));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadCompanies();
    return () => {
      mounted = false;
    };
  }, [t]);

  const filteredCompanies = useMemo(() => {
    return companies.filter(item => {
      if (statusFilter && item.status !== statusFilter) return false;
      if (search && !`${item.name} ${item.industry || ''} ${item.country || ''}`.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [companies, search, statusFilter]);

  const toAbsoluteUrl = path => `${window.location.origin}${path}`;

  const generateGenericLink = async () => {
    setGeneratingLink(true);
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.post('sales/sales-link/');
      const link = response.data?.link || null;
      setGeneratedLink(link);
      setSuccess(t('SALES.SALES_LINK_CREATED', { defaultValue: 'Sales link created' }));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_CREATE_LINK', { defaultValue: 'Unable to create sales link' }));
    } finally {
      setGeneratingLink(false);
    }
  };

  return (
    <>
      <PageHeader title={t('SALES.COMPANIES')} className="mb-3" />

      <Card className="mb-3 border-primary-subtle">
        <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <div className="text-primary fw-semi-bold">{t('SALES.COMPANIES_PANEL')}</div>
            <div className="text-600 fs--1">{t('SALES.COMPANIES_PANEL_HELP')}</div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setQuickCreateMode('company')}>{t('SALES.NEW_COMPANY')}</Button>
            <Button size="sm" variant="outline-primary" onClick={() => setQuickCreateMode('contact')}>{t('SALES.NEW_CONTACT')}</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setQuickCreateMode('opportunity')}>{t('SALES.NEW_OPPORTUNITY')}</Button>
            {canGenerateSalesLinks ? (
              <Button size="sm" variant="outline-dark" onClick={generateGenericLink} disabled={generatingLink}>
                {generatingLink ? t('SALES.SAVING') : t('SALES.GENERATE_LINK', { defaultValue: 'Generate link' })}
              </Button>
            ) : null}
          </div>
        </Card.Body>
      </Card>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}
      {generatedLink ? (
        <Alert variant="info">
          <div className="fw-semi-bold mb-2">{t('SALES.LINK_READY', { defaultValue: 'Lead links ready' })}</div>
          <div className="small">{toAbsoluteUrl(generatedLink.waitlist_path)}</div>
          <div className="small">{toAbsoluteUrl(generatedLink.register_path)}</div>
        </Alert>
      ) : null}

      <Card className="mb-3">
        <Card.Body>
          <div className="row g-3">
            <div className="col-lg-8">
              <InputGroup>
                <InputGroup.Text>{t('SALES.SEARCH')}</InputGroup.Text>
                <Form.Control
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder={t('SALES.SEARCH_COMPANIES_PLACEHOLDER')}
                />
              </InputGroup>
            </div>
            <div className="col-lg-4">
              <Form.Select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
                <option value="">{t('SALES.ALL')}</option>
                <option value="lead">{t('SALES.LEAD')}</option>
                <option value="active_prospect">{t('SALES.ACTIVE_PROSPECT')}</option>
                <option value="customer">{t('SALES.CUSTOMER')}</option>
                <option value="inactive">{t('SALES.INACTIVE')}</option>
                <option value="lost">{t('SALES.LOST')}</option>
              </Form.Select>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>{t('SALES.COMPANY')}</th>
                  <th>{t('SALES.INDUSTRY')}</th>
                  <th>{t('SALES.COUNTRY')}</th>
                  <th>{t('SALES.SOURCE')}</th>
                  <th>{t('SALES.STATUS')}</th>
                  <th>{t('SALES.NEXT_FOLLOWUP')}</th>
                  <th className="text-end">{t('SALES.TABLE_ACTIONS')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredCompanies.length ? filteredCompanies.map(item => (
                  <tr key={item.id}>
                    <td className="fw-semi-bold">{item.name}</td>
                    <td>{item.industry || '-'}</td>
                    <td>{item.country || '-'}</td>
                    <td>{item.source_type || '-'}</td>
                    <td>
                      <Badge bg={item.status === 'customer' ? 'success' : item.status === 'active_prospect' ? 'warning' : 'secondary'}>
                        {item.status}
                      </Badge>
                    </td>
                    <td>{item.next_followup_at ? new Date(item.next_followup_at).toLocaleString() : '-'}</td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-secondary" onClick={() => navigate(`/sales/company/${item.id}`)}>
                        {t('SALES.VIEW_COMPANY')}
                      </Button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={7} className="text-center text-600 py-4">{t('SALES.EMPTY_COMPANIES')}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <SalesQuickCreateModal
        show={Boolean(quickCreateMode)}
        mode={quickCreateMode || 'company'}
        onHide={() => setQuickCreateMode('')}
        companies={companies}
        onCreated={payload => {
          if (payload?.type === 'company' && payload.company) {
            setCompanies(previous => [payload.company, ...previous]);
          }
        }}
        onRequestModeChange={setQuickCreateMode}
      />
    </>
  );
};

export default SalesCompaniesPage;
